// client/src/services/api.js - FIXED VERSION
import axios from 'axios';

// Base URL from environment or default
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// Create axios instance with /api prefix
const apiClient = axios.create({
  baseURL: `${API_BASE_URL}/api`,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Request interceptor - add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - handle common errors
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid - but don't auto-logout on login page
      const isLoginRequest = error.config.url.includes('/auth/login');
      if (!isLoginRequest) {
        localStorage.removeItem('token');
        window.location.href = '/auth/login';
      }
    }
    return Promise.reject(error);
  }
);

// ============================================
// AUTH API
// ============================================
export const authAPI = {
  // Signup - accepts individual parameters including confirmPassword
  signup: async (email, password, confirmPassword, businessName = '', businessType = '') => {
    try {
      console.log('🔐 API: Signup request for:', email);
      const response = await apiClient.post('/auth/signup', {
        email,
        password,
        confirmPassword,
        businessName,
        businessType
      });
      console.log('✅ API: Signup successful');
      return response.data;
    } catch (error) {
      console.error('❌ API: Signup error:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  // Login - accepts object { email, password }
  login: async ({ email, password }) => {
    try {
      console.log('🔐 API: Login request for:', email);
      const response = await apiClient.post('/auth/login', {
        email,
        password
      });
      console.log('✅ API: Login successful');
      return response.data;
    } catch (error) {
      console.error('❌ API: Login error:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  // Verify OTP (6-digit code sent to email)
  verifyOtp: async (email, otp) => {
    try {
      console.log('✉️ API: Verifying OTP for:', email);
      const response = await apiClient.post('/auth/verify-otp', { 
        email, 
        otp 
      });
      console.log('✅ API: OTP verified');
      return response.data;
    } catch (error) {
      console.error('❌ API: Verify OTP error:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  // Resend OTP
  resendOtp: async (email) => {
    try {
      console.log('✉️ API: Resending OTP for:', email);
      const response = await apiClient.post('/auth/resend-otp', { email });
      console.log('✅ API: OTP resent');
      return response.data;
    } catch (error) {
      console.error('❌ API: Resend OTP error:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  // Get current user
  getMe: async () => {
    try {
      console.log('👤 API: Getting current user...');
      const response = await apiClient.get('/auth/me');
      console.log('✅ API: User retrieved');
      return response.data;
    } catch (error) {
      console.error('❌ API: Get user error:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  // Forgot Password - sends reset email
  forgotPassword: async (email) => {
    try {
      const response = await apiClient.post('/auth/forgot-password', { email });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  // Verify reset token validity before showing form
  verifyResetToken: async (token) => {
    try {
      const response = await apiClient.get(`/auth/verify-reset-token/${token}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  // Reset Password with token
  resetPassword: async (token, password, confirmPassword) => {
    try {
      const response = await apiClient.post(`/auth/reset-password/${token}`, { password, confirmPassword });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  // Logout
  logout: () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.href = '/auth/login';
  }
};

// ============================================
// BUSINESS API
// ============================================
export const businessAPI = {
  // Get Profile
  getProfile: async () => {
    try {
      console.log('📖 API: Getting business profile...');
      const response = await apiClient.get('/business/profile');
      console.log('✅ API: Profile retrieved');
      return response.data;
    } catch (error) {
      console.error('❌ API: Get profile error:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  // Save/Update Profile
  saveProfile: async (profileData) => {
    try {
      console.log('💾 API: Saving business profile...');
      const response = await apiClient.post('/business/profile', profileData);
      console.log('✅ API: Profile saved');
      return response.data;
    } catch (error) {
      console.error('❌ API: Save profile error:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  // Delete Profile
  deleteProfile: async () => {
    try {
      console.log('🗑️ API: Deleting business profile...');
      const response = await apiClient.delete('/business/profile');
      console.log('✅ API: Profile deleted');
      return response.data;
    } catch (error) {
      console.error('❌ API: Delete profile error:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  // Mark Phase 1 Complete
  markPhase1Complete: async () => {
    try {
      console.log('✔️ API: Marking phase 1 complete...');
      const response = await apiClient.put('/business/profile/complete');
      console.log('✅ API: Phase 1 marked complete');
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) {
        // Token expired or invalid - redirect to login
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/auth/login';
      }
      console.error('❌ API: Mark complete error:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  // Get Profile Status
  getProfileStatus: async () => {
    try {
      console.log('📊 API: Getting profile status...');
      const response = await apiClient.get('/business/profile/status');
      console.log('✅ API: Status retrieved');
      return response.data;
    } catch (error) {
      console.error('❌ API: Get status error:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  }
};

// Export the axios instance for custom requests
export default apiClient;